import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import _ from 'lodash';

import { logger } from 'utils/logger';

import { showMessageAction } from 'store/global/actions';
import {
  getApplicationBorrowersAction,
  getClosingInfoAction,
  getLoanContactsAction,
  getLoanContactsViewAction,
} from 'store/loanApplications/actions';

import { DRAWER_MESSAGES } from '../../LoanEditorCustomFormDrawer.constants';
import {
  buildEntitySavePromises,
  getSaveValidationErrorMessage,
  logAndDetectSaveRejection,
} from '../../LoanEditorCustomFormDrawer.save';
import { focusFirstInvalidField } from '../../LoanEditorCustomFormDrawer.utils';

import {
  collectInvalidDateFieldNames,
  getSelectionRequiredMessage,
  showErrorMessage,
  withInvalidDateErrors,
} from './utils';

const useDrawerSave = ({
  contentRef,
  hasAnyEntityData,
  requiresBorrowerSelection,
  requiresContactSelection,
  requiresClosingCompanySelection,
  validateForm,
  setValidationErrors,
  areCustomFieldsValid,
  markAllCustomFieldsTouched,
  hasCustomFieldChanges,
  saveCustomFields,
  entitySaveContext,
  setIsSaving,
  onClose,
}) => {
  const dispatch = useDispatch();

  return useCallback(async () => {
    if (!hasAnyEntityData) {
      return null;
    }

    const selectionRequiredMessage = getSelectionRequiredMessage({
      requiresBorrowerSelection,
      requiresContactSelection,
      requiresClosingCompanySelection,
    });

    if (selectionRequiredMessage) {
      showErrorMessage({ dispatch, message: selectionRequiredMessage });

      return null;
    }

    const isLoanFormValid = await validateForm({ noFormChangedValidate: true });
    const invalidDateFieldNames = collectInvalidDateFieldNames(contentRef.current);
    const validationErrorMessage = getSaveValidationErrorMessage({
      isLoanFormValid: isLoanFormValid && !invalidDateFieldNames.length,
      areCustomFieldsValid,
    });

    if (validationErrorMessage) {
      if (!areCustomFieldsValid) {
        markAllCustomFieldsTouched();
      }

      if (invalidDateFieldNames.length) {
        setValidationErrors(validationErrors =>
          withInvalidDateErrors({ validationErrors, fieldNames: invalidDateFieldNames }),
        );
      }

      showErrorMessage({ dispatch, message: validationErrorMessage });
      window.requestAnimationFrame(() => focusFirstInvalidField(contentRef.current));

      return null;
    }

    setIsSaving(true);

    const entitySavePromises = buildEntitySavePromises({ dispatch, ...entitySaveContext });
    const customFieldsSavePromise = hasCustomFieldChanges
      ? saveCustomFields()
      : Promise.resolve(null);

    const saveResults = await Promise.allSettled([
      ...Object.values(entitySavePromises),
      customFieldsSavePromise,
    ]);

    setIsSaving(false);

    if (logAndDetectSaveRejection(saveResults)) {
      return null;
    }

    const {
      applicationId,
      loanId,
      hasBorrowerChanges,
      hasContactChanges,
      hasClosingChanges,
      hasFundingChanges,
    } = entitySaveContext;

    if (hasBorrowerChanges && !_.isNil(applicationId)) {
      dispatch(getApplicationBorrowersAction({ applicationId })).catch(error =>
        logger.error(error),
      );
    }

    if (hasContactChanges && !_.isNil(applicationId)) {
      dispatch(getLoanContactsAction({ applicationId })).catch(error => logger.error(error));
      // The Contacts tab and universal search list the loan's contacts from the view.
      dispatch(getLoanContactsViewAction({ applicationId })).catch(error => logger.error(error));
    }

    if (hasClosingChanges || hasFundingChanges) {
      dispatch(getClosingInfoAction({ loanId })).catch(error => logger.error(error));
    }

    dispatch(showMessageAction({ message: DRAWER_MESSAGES.SAVE_SUCCESS, variant: 'success' }));
    onClose();

    return null;
  }, [
    areCustomFieldsValid,
    contentRef,
    dispatch,
    entitySaveContext,
    hasAnyEntityData,
    hasCustomFieldChanges,
    markAllCustomFieldsTouched,
    onClose,
    requiresBorrowerSelection,
    requiresClosingCompanySelection,
    requiresContactSelection,
    saveCustomFields,
    setIsSaving,
    setValidationErrors,
    validateForm,
  ]);
};

export default useDrawerSave;
